// ── WebGPU Renderer with Automatic Fallback ──
// Phase 12: WebGPU migration infrastructure.
// Creates the best available renderer (WebGPU → WebGL2 fallback chain).
// Currently always uses WebGL2 because pmndrs/postprocessing is WebGL-only.

import * as THREE from 'three';

export interface RendererCapabilities {
  isWebGPU: boolean;
  backend: 'webgpu' | 'webgl2';
  maxTextureSize: number;
  maxAnisotropy: number;
}

/**
 * Create the best available renderer with automatic fallback.
 * WebGPU → WebGL2 fallback chain.
 *
 * Even when WebGPU is available, we currently fall back to WebGL2 because
 * pmndrs/postprocessing (EffectComposer, N8AO, SSR, LUT, etc.) is
 * incompatible with Three.js WebGPURenderer. This function probes for
 * WebGPU support and stores the result so other systems can query it.
 */
export async function createBestRenderer(
  canvas: HTMLCanvasElement,
  options: {
    antialias?: boolean;
    alpha?: boolean;
    powerPreference?: string;
    preferWebGPU?: boolean;
  } = {},
): Promise<{ renderer: THREE.WebGLRenderer; capabilities: RendererCapabilities }> {
  const {
    antialias = true,
    alpha = false,
    powerPreference = 'high-performance',
    preferWebGPU = false,
  } = options;

  // Probe WebGPU availability (non-blocking, for future use)
  if (preferWebGPU && 'gpu' in navigator) {
    try {
      const adapter = await (navigator as any).gpu.requestAdapter();
      if (adapter) {
        // Store capability for future migration — not used as production renderer yet
        // because pmndrs/postprocessing is incompatible with WebGPURenderer.
        (window as any).__webgpuAvailable = true;
        (window as any).__webgpuAdapterInfo = {
          vendor: adapter.info?.vendor || 'unknown',
          architecture: adapter.info?.architecture || 'unknown',
        };

        // R6 — surface the migration readiness checklist so dev tooling and
        // /handoff snapshots can see which pmndrs effects still block the
        // WebGPU swap. Cheap to compute; only logged when WebGPU is present.
        const { getWebGPUPostProcessingReadiness } = await import('./webgpu-postprocessing');
        const readiness = getWebGPUPostProcessingReadiness();
        (window as any).__webgpuPostprocessingReadiness = readiness;
      }
    } catch {
      // WebGPU probe failed — silently fall back to WebGL2
    }
  }

  // Create WebGL2 renderer (current production path)
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias,
    alpha,
    powerPreference: powerPreference as WebGLPowerPreference,
    stencil: false,
    depth: true,
  });

  renderer.outputColorSpace = THREE.SRGBColorSpace;
  // R-C-001: pmndrs/postprocessing applies ACES via ToneMappingEffect in the
  // EffectPass, so the host renderer must stay at NoToneMapping. Otherwise
  // ACES is applied twice (once per render-target write, once in the effect)
  // and the scene reads as washed out / over-bright.
  renderer.toneMapping = THREE.NoToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  const capabilities: RendererCapabilities = {
    isWebGPU: false,
    backend: 'webgl2',
    maxTextureSize: renderer.capabilities.maxTextureSize,
    maxAnisotropy: renderer.capabilities.getMaxAnisotropy(),
  };

  return { renderer, capabilities };
}

/**
 * Check if the browser supports WebGPU.
 */
export function isWebGPUSupported(): boolean {
  return 'gpu' in navigator;
}

/**
 * Get WebGPU adapter info (if available).
 * Returns null only on unexpected errors; otherwise returns an object
 * with `supported: false` when WebGPU is not present.
 */
export async function getWebGPUInfo(): Promise<{
  supported: boolean;
  vendor?: string;
  architecture?: string;
  features?: string[];
} | null> {
  if (!('gpu' in navigator)) return { supported: false };

  try {
    const adapter = await (navigator as any).gpu.requestAdapter();
    if (!adapter) return { supported: false };

    return {
      supported: true,
      vendor: adapter.info?.vendor,
      architecture: adapter.info?.architecture,
      features: Array.from(adapter.features || []),
    };
  } catch {
    return { supported: false };
  }
}
